/**
 * 发品/商品模板：类目属性里的「品牌名」选项来自店铺品牌缓存（bg.goods.brand.get），
 * 而非 attrs.get 的 values（平台通常不在类目属性里下发品牌枚举）。
 */
import { flattenBrandRows } from '@/temu-platform/utils/temuBrandIp';

/** 是否品牌类目属性（名称 / 英文名） */
export function isTemuBrandAttribute(attr, rawAttr = null) {
  const name = [
    attr?.name,
    attr?.nameEn,
    rawAttr?.attribute_name,
    rawAttr?.attribute_name_cn,
    rawAttr?.attribute_name_en,
    rawAttr?.temu_prop?.name,
  ].map((s) => String(s || '').trim()).filter(Boolean).join(' ');
  if (!name) return false;
  if (/品牌/.test(name)) return true;
  return /\bbrands?\b/i.test(name);
}

/** 店铺品牌缓存 → AttributeField 选项（id 优先 vid，提交 productPropertyReqs.vid） */
export function shopBrandsToAttributeValues(brandsRaw) {
  return flattenBrandRows(brandsRaw)
    .filter((row) => row.brandCode && row.brandCode !== '—')
    .map((row) => {
      const vid = String(row.vid || row.brandCode).trim();
      const label = row.brandName && row.brandName !== '—'
        ? String(row.brandName).trim()
        : row.brandCode;
      return {
        id: vid,
        name: label,
        nameCn: label,
        brandId: row.brandCode,
      };
    })
    .filter((row) => row.id && row.name);
}

function patchAttrList(list, brandAttrIds, brandValues) {
  return (Array.isArray(list) ? list : []).map((attr) => {
    if (!attr || !brandAttrIds.has(String(attr.attributeId))) return attr;
    return {
      ...attr,
      values: brandValues.map(({ id, name, nameCn, brandId }) => ({
        id,
        name,
        nameCn,
        brandId,
      })),
    };
  });
}

function resolveRawAttributeInfos(attrTpl) {
  const raw = attrTpl?.raw;
  if (!raw || typeof raw !== 'object') return [];
  if (Array.isArray(raw.data?.[0]?.attribute_infos)) return raw.data[0].attribute_infos;
  if (Array.isArray(raw.attribute_infos)) return raw.attribute_infos;
  if (Array.isArray(raw.data)) {
    const merged = [];
    raw.data.forEach((row) => {
      if (Array.isArray(row?.attribute_infos)) merged.push(...row.attribute_infos);
    });
    return merged;
  }
  return [];
}

/**
 * 将店铺品牌写入类目属性模板中的品牌字段（含 raw，供发品组装 vid）。
 * @returns {object} 新的 attrTpl（无品牌属性或无品牌数据时原样返回）
 */
export function applyShopBrandsToAttrTemplate(attrTpl, brandsRaw) {
  if (!attrTpl || typeof attrTpl !== 'object') return attrTpl;
  const brandValues = shopBrandsToAttributeValues(brandsRaw);
  if (!brandValues.length) return attrTpl;

  const rawInfos = resolveRawAttributeInfos(attrTpl);
  const brandAttrIds = new Set();

  (attrTpl.productAttrs || []).forEach((attr) => {
    const raw = rawInfos.find(
      (row) => String(row?.attribute_id ?? row?.attributeId) === String(attr?.attributeId),
    );
    if (isTemuBrandAttribute(attr, raw)) {
      brandAttrIds.add(String(attr.attributeId));
    }
  });

  if (!brandAttrIds.size) {
    // 兜底：仅按展示名识别（raw 可能尚未带 temu_prop）
    (attrTpl.productAttrs || []).forEach((attr) => {
      if (isTemuBrandAttribute(attr)) brandAttrIds.add(String(attr.attributeId));
    });
  }
  if (!brandAttrIds.size) return attrTpl;

  rawInfos.forEach((row) => {
    const id = String((row?.attribute_id ?? row?.attributeId) || '').trim();
    if (!id || !brandAttrIds.has(id)) return;
    row.attribute_value_info_list = brandValues.map((v) => ({
      attribute_value_id: v.id,
      attribute_value: v.name,
      attribute_value_name: v.name,
      attribute_value_en: v.name,
      temu_vid: v.id,
      temu_brand_id: v.brandId,
    }));
  });

  return {
    ...attrTpl,
    productAttrs: patchAttrList(attrTpl.productAttrs, brandAttrIds, brandValues),
    requiredProduct: patchAttrList(attrTpl.requiredProduct, brandAttrIds, brandValues),
    optionalProduct: patchAttrList(attrTpl.optionalProduct, brandAttrIds, brandValues),
    mainAttrs: patchAttrList(attrTpl.mainAttrs, brandAttrIds, brandValues),
    otherAttrs: patchAttrList(attrTpl.otherAttrs, brandAttrIds, brandValues),
  };
}

function collectBrandAttributeDefs(attrTpl) {
  const pool = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.optionalProduct || []),
    ...(attrTpl?.mainAttrs || []),
    ...(attrTpl?.otherAttrs || []),
  ];
  const rawInfos = resolveRawAttributeInfos(attrTpl);
  const seen = new Set();
  const out = [];
  pool.forEach((attr) => {
    const id = String(attr?.attributeId ?? '').trim();
    if (!id || seen.has(id)) return;
    const raw = rawInfos.find(
      (row) => String(row?.attribute_id ?? row?.attributeId) === id,
    );
    if (!isTemuBrandAttribute(attr, raw)) return;
    seen.add(id);
    out.push(attr);
  });
  return out;
}

function mapBrandSelectionToken(token, values) {
  const text = String(token ?? '').trim();
  if (!text) return text;
  const list = Array.isArray(values) ? values : [];
  const byVid = list.find((row) => String(row?.id ?? '').trim() === text);
  if (byVid) return String(byVid.id);
  const byBrandId = list.find((row) => String(row?.brandId ?? '').trim() === text);
  if (byBrandId) return String(byBrandId.id);
  const byName = list.find((row) => (
    String(row?.name || '').trim() === text
    || String(row?.nameCn || '').trim() === text
    || String(row?.label || '').trim() === text
  ));
  if (byName) return String(byName.id);
  return text;
}

/**
 * 模板/草稿里品牌常存 brandId；提交须用店铺品牌 vid。
 * 在已注入店铺品牌选项的 attrTpl 上，把 brandId / 名称归一成 vid。
 */
export function normalizeBrandAttributeSelections(productAttributes = {}, attrTpl) {
  if (!attrTpl || typeof attrTpl !== 'object') return productAttributes || {};
  const brandAttrs = collectBrandAttributeDefs(attrTpl);
  if (!brandAttrs.length) return productAttributes || {};

  const next = { ...(productAttributes || {}) };
  let changed = false;

  brandAttrs.forEach((attr) => {
    const attributeId = String(attr?.attributeId ?? '').trim();
    if (!attributeId || !(attributeId in next)) return;
    const raw = next[attributeId];
    if (raw == null || raw === '') return;
    const values = Array.isArray(attr?.values) ? attr.values : [];
    if (!values.length) return;

    if (Array.isArray(raw)) {
      const mapped = raw.map((entry) => {
        if (entry && typeof entry === 'object') {
          const valueId = entry.valueId ?? entry.attributeValueId ?? entry.vid ?? entry.id;
          const nextId = mapBrandSelectionToken(valueId, values);
          if (String(nextId) === String(valueId ?? '')) return entry;
          return { ...entry, valueId: nextId };
        }
        return mapBrandSelectionToken(entry, values);
      });
      if (JSON.stringify(mapped) !== JSON.stringify(raw)) {
        next[attributeId] = mapped;
        changed = true;
      }
      return;
    }

    const mapped = mapBrandSelectionToken(raw, values);
    if (String(mapped) !== String(raw).trim()) {
      next[attributeId] = mapped;
      changed = true;
    }
  });

  return changed ? next : (productAttributes || {});
}
